const MS_PER_DAY = 24 * 60 * 60 * 1000;

const DAY_MONTH_FR = new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short" });

const toUtcDay = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

/**
 * Build a contiguous day-by-day series from a sparse set of API points so the
 * chart never has holes. Days with no data are filled with zeros. Dates are
 * pinned to the start of the UTC day to match the server's date_trunc('day').
 */
export interface SeriesAlignmentInput<T> {
    startDate: Date;
    endDate: Date;
    points: T[];
    keyDate: (point: T) => string;
    zero: () => Omit<T, "date"> | T;
}

export const fillDailyGaps = <T extends { date: string }>(
    startDate: Date,
    endDate: Date,
    points: T[],
    zero: () => Omit<T, "date"> | T,
): T[] => {
    const pointsByDay = new Map<number, T>();
    for (const p of points) {
        const d = new Date(p.date);
        if (Number.isNaN(d.getTime())) continue;
        pointsByDay.set(toUtcDay(d), p);
    }

    const result: T[] = [];
    const startUtc = toUtcDay(startDate);
    const endUtc = toUtcDay(endDate);

    for (let utc = startUtc; utc <= endUtc; utc += MS_PER_DAY) {
        const iso = new Date(utc).toISOString();
        const existing = pointsByDay.get(utc);
        if (existing) {
            result.push({ ...existing, date: iso });
        } else {
            const z = zero();
            result.push({ ...(z as any), date: iso } as T);
        }
    }
    return result;
};

/**
 * Return a copy of `d` pinned to 00:00:00.000 local time (start of day).
 * Used to make analytics date ranges fully inclusive on the start side.
 */
export const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

/**
 * Return a copy of `d` pinned to 23:59:59.999 local time (end of day).
 * Used to make analytics date ranges fully inclusive on the end side —
 * otherwise picking "today" would exclude any event logged after midnight.
 */
export const endOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

/**
 * Latest calendar end date for the end picker so that, after the same
 * `startOfDay` / `endOfDay` normalization used on Apply, the span never
 * exceeds `maxRangeDays * 24h` (matches analytics / reviews / export APIs).
 *
 * Plain `start + maxRangeDays * MS_PER_DAY` is one day too loose because
 * `endOfDay(lastPick)` adds almost another full day vs that midnight bound.
 */
export function maxEndDateForAnalyticsStyleRange(start: Date, maxRangeDays: number): Date {
    const s = startOfDay(start).getTime();
    const maxParsedEndMs = s + maxRangeDays * MS_PER_DAY;
    let d = startOfDay(new Date(maxParsedEndMs - MS_PER_DAY));
    for (let i = 0; i < 8; i++) {
        if (endOfDay(d).getTime() <= maxParsedEndMs) return d;
        d = new Date(d.getTime() - MS_PER_DAY);
    }
    return d;
}

/** Earliest calendar start date for the start picker under the same API rule. */
export function minStartDateForAnalyticsStyleRange(end: Date, maxRangeDays: number): Date {
    const maxParsedEndMs = endOfDay(end).getTime();
    const maxSpanMs = maxRangeDays * MS_PER_DAY;
    let d = startOfDay(new Date(maxParsedEndMs - maxSpanMs + 1));
    for (let i = 0; i < 8; i++) {
        if (maxParsedEndMs - startOfDay(d).getTime() <= maxSpanMs) return d;
        d = new Date(d.getTime() + MS_PER_DAY);
    }
    return d;
}

/** "YYYY-MM-DD" for a Date, in UTC — matches the download-stats backend. */
export const toUtcDayKey = (d: Date): string => d.toISOString().slice(0, 10);

/** Calendar day the user picked in the date selector (local, not UTC-shifted). */
export const toLocalDayKey = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

/** Subtract calendar days from a UTC day key. */
export const subtractUtcDayKey = (dayKey: string, days: number): string => {
    const d = new Date(`${dayKey.slice(0, 10)}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() - days);
    return toUtcDayKey(d);
};

export interface PlatformFreshness {
    ios: string | null;
    android: string | null;
}

/** Extra margin: last ingested day may still be consolidating in store reports. */
export const DOWNLOAD_FRESHNESS_SAFETY_DAYS = 1;

/** Last UTC day we treat as reliable for display and range gating. */
export const getSafeFreshnessKey = (
    dayKey: string | null | undefined,
    safetyDays = DOWNLOAD_FRESHNESS_SAFETY_DAYS,
): string | null => {
    if (!dayKey) return null;
    return subtractUtcDayKey(dayKey, safetyDays);
};

export const getSafeFreshness = (
    freshness: PlatformFreshness,
    safetyDays = DOWNLOAD_FRESHNESS_SAFETY_DAYS,
): PlatformFreshness => ({
    ios: getSafeFreshnessKey(freshness.ios, safetyDays),
    android: getSafeFreshnessKey(freshness.android, safetyDays),
});

/**
 * True when the selected end date reaches beyond what store reports can
 * reliably cover. Compares the user's picked calendar day against the
 * tighter of iOS/Android safe freshness (UTC day keys from the pipeline).
 */
export const isDownloadRangeBeyondFreshness = (
    endDate: Date,
    freshness: PlatformFreshness,
    safetyDays = DOWNLOAD_FRESHNESS_SAFETY_DAYS,
): boolean => {
    const endKey = toLocalDayKey(endDate);
    const safeLatestKeys = (["ios", "android"] as const)
        .map((platform) => getSafeFreshnessKey(freshness[platform], safetyDays))
        .filter((key): key is string => Boolean(key));

    if (safeLatestKeys.length === 0) return false;

    const maxSafeEnd = safeLatestKeys.reduce((min, key) => (key < min ? key : min));
    return endKey > maxSafeEnd;
};

/** Short axis label, e.g. "12 avr." in French. */
export const formatAxisDate = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return DAY_MONTH_FR.format(d);
};
